import React, { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { OverflowMenuVertical } from '@carbon/icons-react';
import { Badge, IconButton, makeStyles } from '@material-ui/core';
import {
  CapaQuotationChatRoomAnnotationComment,
  useFetchMyQuotationUserQuery,
  useFetchQuotationPartnerQuery
} from '@generated/graphql';
import { RootState } from '@modules/index';
import errorMessage from '@utils/errorMessage';
import { annotationTimeFormatter } from '@utils/formatter';
import { CustomizedButton, CustomizedTypography } from '@components/Customized';
import { colorSet, useSnackbar } from '@components/Provider';
import { AnnotationListProps } from './AnnotationList';
import AnnotationControlPopup from './AnnotationControlPopup';
import AnnotationDeleteDialog from './AnnotationDeleteDialog';
import useViewerApiContext from '../context/useViewerApiContext';
import UserAvatar from './UserAvatar';

const AnnotationListEntry = ({
  annotation,
  onClickAnnotation
}: Pick<AnnotationListProps, 'onClickAnnotation'> & {
  annotation: CapaQuotationChatRoomAnnotationComment;
}) => {
  const partnerId = useSelector<RootState>(
    (state) => state.chat.quotationPartnerId
  ) as string;
  const { onDeleteAnnotation } = useViewerApiContext();
  const {
    annotationContainer,
    entryHeader,
    commentInfo,
    showReplies
  } = useStyles();
  const popupAnchor = useRef<HTMLAnchorElement | null>(null);
  const [openControlPopup, setOpenControlPopup] = useState<boolean>(false);
  const [openAnnotationDeleteDialog, setOpenAnnotationDeleteDialog] = useState<
    boolean
  >(false);
  const { data: myInfo } = useFetchMyQuotationUserQuery({
    fetchPolicy: 'cache-only'
  });
  const { data: partnerInfo } = useFetchQuotationPartnerQuery({
    variables: {
      quotationPartnerId: partnerId
    },
    fetchPolicy: 'cache-first',
    skip: !partnerId
  });
  const { enqueueSnackbar } = useSnackbar();

  const clientName = myInfo?.fetchMyQuotationUser.data?.managerName ?? '';

  // 튜토리얼시 파트너 이름 제외한 나머지 coomentUser를 파트너 이름으로 설정
  const partnerName =
    partnerInfo?.fetchQuotationPartner.data?.managerName ??
    (annotation.commentUsers ?? []).find((v) => v.userName !== clientName)
      ?.userName ??
    '';

  const annotationComment = annotation?.comments?.[0];
  const authorName = annotationComment?.userName;
  const isMyAnnotation = authorName === clientName;
  const hasReply = (annotation.commentUsers ?? []).length > 1;

  const onOpenDeleteAnnotationDialog = () => {
    setOpenAnnotationDeleteDialog(true);
  };

  const deleteAnnotation = async () => {
    if (!onDeleteAnnotation) return;
    try {
      await onDeleteAnnotation(annotation.annotationId as string);
    } catch {
      enqueueSnackbar(errorMessage.deleteAnnotation);
    } finally {
      setOpenAnnotationDeleteDialog(false);
    }
  };

  return (
    <>
      <AnnotationDeleteDialog
        open={openAnnotationDeleteDialog}
        onClose={() => setOpenAnnotationDeleteDialog(false)}
        onDelete={deleteAnnotation}
      />
      <AnnotationControlPopup
        anchorRef={popupAnchor}
        open={openControlPopup}
        onClose={() => setOpenControlPopup(false)}
        onDelete={onOpenDeleteAnnotationDialog}
      />
      <div
        className={annotationContainer}
        onClick={(e) => {
          e.stopPropagation();
          if (openControlPopup) return;
          onClickAnnotation(
            e,
            annotation.fileId as string,
            annotation.annotationId as string
          );
        }}
      >
        <div className={entryHeader}>
          {annotation.isNew && (
            <Badge variant="dot" className="new-badge" color="error" />
          )}
          <UserAvatar userName={isMyAnnotation ? clientName : partnerName} />
          {hasReply && (
            <UserAvatar userName={isMyAnnotation ? partnerName : clientName} />
          )}
          {isMyAnnotation && (
            <IconButton
              component="a"
              ref={popupAnchor}
              className="more-button"
              onClick={(e: any) => {
                e.stopPropagation();
                setOpenControlPopup(true);
              }}
            >
              <OverflowMenuVertical size={20} color={colorSet.gray700} />
            </IconButton>
          )}
        </div>
        <CustomizedTypography
          variant="subtitle2"
          color="textSecondary"
          className="file-name"
          noWrap
        >
          {annotation.fileName}
        </CustomizedTypography>
        <div className={commentInfo}>
          <CustomizedTypography variant="subtitle2" fontWeight="500">
            {isMyAnnotation ? clientName : partnerName}
          </CustomizedTypography>
          <CustomizedTypography variant="subtitle2" className="gray500">
            {annotationTimeFormatter(annotation.createdAt as string)}
          </CustomizedTypography>
        </div>
        <CustomizedTypography variant="body2" className="gray900">
          {annotation.comments?.[0].comment}
        </CustomizedTypography>
        {(annotation.comments ?? []).length > 1 && (
          <CustomizedButton
            variant="text"
            color="primary"
            className={showReplies}
          >
            <CustomizedTypography
              variant="subtitle2"
              color="inherit"
              fontWeight="normal"
            >
              {(annotation.comments ?? []).length - 1}개의 답글
            </CustomizedTypography>
          </CustomizedButton>
        )}
      </div>
    </>
  );
};

export default AnnotationListEntry;

const useStyles = makeStyles((theme) => ({
  annotationContainer: {
    display: 'flex',
    flexDirection: 'column',
    padding: '12px 20px',
    gap: '8px',

    '& .gray500': {
      color: colorSet.gray500
    },
    '&:hover': {
      backgroundColor: colorSet.gray100,
      cursor: 'pointer'
    }
  },
  entryHeader: {
    display: 'flex',
    gap: '4px',
    '& .more-button': {
      width: 32,
      height: 32,
      padding: '6px',
      marginLeft: 'auto'
    },
    '& .new-badge': {
      '& .MuiBadge-dot': {
        height: '4px',
        width: '4px',
        minWidth: '4px',
        borderRadius: '2px',
        backgroundColor: colorSet.errorBase
      },
      '& .MuiBadge-anchorOriginTopRightRectangle': {
        top: '50%',
        left: '-12px'
      }
    }
  },
  commentInfo: {
    display: 'flex',
    gap: '4px'
  },
  showReplies: {
    '& span': {
      justifyContent: 'flex-start'
    }
  }
}));
